import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { useLazyQuery } from '@apollo/react-hooks';
import { RichText } from 'prismic-reactjs';
import { useFormik } from 'formik';
import { Q_BUSINESSES } from '../../graphql/business';
import { useAuth, useCategories, useSearchFilter, useWindowSize } from '../../hooks';
import { Client } from '../../prismic-configuration';
import { PrismicSearchDoc } from '../../types/prismic';
import { Routes } from '../../helpers/constants';
import { BackToTopButton, PageWrapper } from '../../components/base';
import {
  SearchFilter,
  SearchHeader,
  PillFilter,
  LabelFilter,
  BusinessThumb,
  FilterToggle,
} from '../../components/search';
import ButtonArrow from '../../components/buttons/ButtonArrow';
import LoadingIndicator from '../../components/base/LoadingIndicator';
import CrossIcon from '../../components/buttons/CrossIcon';
import useScrollPosition from '../../helpers/useScrollPosition';
import styles from './search.module.sass';

enum Sort {
  // RANDOM = 'RANDOM',
  ALPHABETICALLY = 'ALPHABETICALLY',
  LATEST = 'LATEST',
}

const OwnershipOptions = [
  {
    value: 'blackOwned',
    label: 'Black-Owned',
  },
  {
    value: 'indigenousOwned',
    label: 'Indigenous-Owned',
  },
  // TODO: Once brand profiles have been updated, this feature could be switched on (needs testing first)
  // {
  //   value: 'majorityWomenOwned',
  //   label: 'Majority-Women-Owned',
  // },
];

const SortOptions = [
  // {
  //   value: Sort.RANDOM,
  //   label: 'Random',
  // },
  {
    value: Sort.LATEST,
    label: 'Latest',
  },
  {
    value: Sort.ALPHABETICALLY,
    label: 'A - Z',
  },
];

enum FilterTypes {
  NONE,
  OWNERSHIP,
  CATEGORIES,
  SORT,
}

type Props = {
  doc: PrismicSearchDoc;
  searchQuery?: string;
  filter: {
    categories?: string[];
    sort?: string;
  };
};

interface Country {
  country: string | undefined;
}

interface Filter {
  locations?: Country[] | undefined;
  blackOwned?: boolean | undefined;
  // blackPartners?: boolean | undefined;
  blackFounded?: boolean | undefined;
  // blackFronted?: boolean | undefined;
  indigenousOwned?: boolean | undefined;
  // majorityWomenOwned?: boolean | undefined;
  // indigenousPartners?: boolean | undefined;
  // indigenousFounded?: boolean | undefined;
  // indigenousFronted?: boolean | undefined;
  // ethicallySustainable?: boolean | undefined;
  hasFactor?: boolean | undefined;
  hasCredit?: boolean | undefined;
  hasInvestors?: boolean | undefined;
  wantsInvestors?: boolean | undefined;
  // revenue?: {
  //   min?: number | undefined;
  //   max?: number | undefined;
  // };
}

let loadingDelay = null;

const SearchPage = ({ doc, searchQuery, filter }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { categories } = useCategories();

  const isAdmin =
    user && user.data.adminRole && user.data.adminRole.toUpperCase() === 'ADMIN';
  const isCorporationUser =
    user && user.corporation && user.corporation.corporation.data.isValidated;

  const showCorporationFilters = isAdmin || isCorporationUser;

  const {
    searchInput,
    setSearchInput,
    filterCategories,
    setFilterCategories,
  } = useSearchFilter();

  const observer = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // const [shouldUpdate, setShouldUpdate] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const scrollPosition = useScrollPosition();
  const { windowWidth } = useWindowSize();
  const [maxFiltersHeight, setMaxFiltersHeight] = useState(0);
  const [filtersTop, setFiltersTop] = useState(100);

  const [cursor, setCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [businesses, setBusinesses] = useState<any>([]);
  const [minimized, setMinimized] = useState(false);
  const [filterSort, setFilterSort] = useState(filter.sort.toUpperCase());
  const [filterType, setFilterType] = useState(FilterTypes.NONE);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [businessesFilter, setBusinessesFilter] = useState<Filter>({
    locations: [
      {
        country: '',
      },
    ],
    blackOwned: undefined,
    // blackPartners: undefined,
    blackFounded: undefined,
    // blackFronted: undefined,
    indigenousOwned: undefined,
    // majorityWomenOwned: undefined,
    // indigenousPartners: undefined,
    // indigenousFounded: undefined,
    // indigenousFronted: undefined,
    // ethicallySustainable: undefined,
    hasFactor: undefined,
    hasCredit: undefined,
    hasInvestors: undefined,
    wantsInvestors: undefined,
    // revenue: undefined,
  });

  const [getBusinesses, { data, loading, error }] = useLazyQuery(Q_BUSINESSES);

  useEffect(() => {
    setFilterCategories(filter.categories);
    setSearchInput(searchQuery);
    if (filter.categories && filter.categories.length)
      setFilterType(FilterTypes.CATEGORIES);
  }, []);

  useEffect(() => {
    updateBusinesses();
  }, [categories]);

  const lastBusinessRef = useCallback(
    (node) => {
      if (loading) return;

      if (!observer) return;
      observer.current && observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          // setShouldUpdate(false);
          updateBusinesses();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasNextPage, cursor]
  );

  useEffect(() => {
    setSearchInput(searchQuery);
    setFilterCategories(filter.categories);
    if (filter.categories && filter.categories.length)
      setFilterType(FilterTypes.CATEGORIES);
  }, []);

  useEffect(() => {
    updateBusinesses();
  }, [categories]);

  // useLayoutEffect(() => {
  //   if (!showCorporationFilters) return;
  //   if (!filtersRef || !filtersRef.current) return;
  //   const filters = filtersRef.current;
  //   const filtersTop = filters.getBoundingClientRect().top;
  //   setMaxFiltersHeight(window.innerHeight - filtersTop);
  // }, [scrollPosition]);

  useEffect(() => {
    if (!showCorporationFilters) return;
    if (!searchRef || !searchRef.current) return;
    if (!filtersRef || !filtersRef.current) return;
    const filters = filtersRef.current;
    const filtersTop = filters.getBoundingClientRect().top;

    const search = searchRef.current;
    const searchRect = search.getBoundingClientRect();
    const searchHeight = searchRect.height;
    if (windowWidth < 1024) {
      setFiltersTop(searchHeight + searchRect.top);
      setMaxFiltersHeight(window.innerHeight - searchRect.top - searchHeight);
    } else {
      setFiltersTop(searchHeight);
      setMaxFiltersHeight(window.innerHeight - filtersTop);
    }
  }, [windowWidth, scrollPosition, filterType]);

  useEffect(() => {
    if (!data) return;
    const { pageInfo } = data.businesses;
    const { hasPreviousPage, hasNextPage, endCursor } = pageInfo;
    setHasNextPage(hasNextPage);

    if (hasNextPage) setCursor(endCursor);

    if (hasPreviousPage) {
      setBusinesses((prevBusinesses) => [
        ...prevBusinesses,
        ...data.businesses.edges,
      ]);
    } else {
      setBusinesses(data.businesses.edges);
    }
  }, [data]);

  useEffect(() => {
    if (loading) {
      loadingDelay = setTimeout(() => {
        setShowLoading(true);
      }, 200);
    } else {
      if (loadingDelay) {
        setShowLoading(false);
        clearTimeout(loadingDelay);
      }
    }

    return () => {
      if (loadingDelay) clearTimeout(loadingDelay);
    };
  }, [loading]);

  useEffect(() => {
    // setShouldUpdate(true);
    updateBusinesses();
  }, [filterCategories, filterSort, businessesFilter]);

  useEffect(() => {
    setCursor(null);
    updateBusinesses(true);
    console.log(window.scrollY, filtersTop);
    if (window.scrollY >= filtersTop) {
      window.scrollTo(0, filtersTop);
    }
  }, [searchInput]);

  const updateBusinesses = (noCursor = false) => {
    if (loading) return;
    getBusinesses({
      variables: {
        text: searchInput && searchInput.length ? searchInput : '',
        sort: filterSort,
        primaryCategories:
          filterCategories && filterCategories.length ? filterCategories : null,
        locations:
          businessesFilter.locations[0].country == ''
            ? undefined
            : businessesFilter.locations,
        blackOwned: businessesFilter.blackOwned,
        // blackPartners: businessesFilter.blackPartners,
        blackFounded: businessesFilter.blackFounded,
        // blackFronted: businessesFilter.blackFronted,
        indigenousOwned: businessesFilter.indigenousOwned,
        // majorityWomenOwned: businessesFilter.majorityWomenOwned,
        // indigenousPartners: businessesFilter.indigenousPartners,
        // indigenousFounded: businessesFilter.indigenousFounded,
        // indigenousFronted: businessesFilter.indigenousFronted,
        // ethicallySustainable: businessesFilter.ethicallySustainable,
        hasFactor: businessesFilter.hasFactor,
        hasCredit: businessesFilter.hasCredit,
        hasInvestors: businessesFilter.hasInvestors,
        wantsInvestors: businessesFilter.wantsInvestors,
        // revenue: businessesFilter.revenue,
        cursor: noCursor ? null : cursor,
        first: 12,
      },
    });

    // if (!shouldUpdate) return;

    let existingCategories = null;
    if (categories && filterCategories && filterCategories.length) {
      existingCategories = filterCategories.filter((value) => {
        return _.find(
          categories.data.categories_primary,
          _.matchesProperty('value', value)
        );
      });
    }

    const query: any = {};

    if (searchInput && searchInput.length) query.q = searchInput;
    query.sort = filterSort;
    if (existingCategories && existingCategories.length)
      query.cat = existingCategories.join();

    if (
      router.query.q === query.q &&
      router.query.cat === query.cat &&
      router.query.sort === query.sort
    )
      return;

    router.push(
      {
        pathname: Routes.SHOP_BLACK_SEARCH,
        query,
      },
      null,
      { shallow: true }
    );
  };

  const handleSearchChange = (e) => {
    const value = e.currentTarget.value;
    formik.setFieldValue('search', value);
    if (value === '') setSearchInput('');
  };

  const handleClearSearch = () => {
    formik.setFieldValue('search', '');
    setSearchInput('');
  };

  const handleSearchSubmit = (values) => {
    const input = values.search;
    const query: any = {
      q: input,
    };
    if (router.query.sort) query.sort = router.query.sort;
    if (router.query.cat) query.cat = router.query.cat;
    setSearchInput(input);

    router.push({
      pathname: Routes.SHOP_BLACK_SEARCH,
      query,
    });
  };

  const handleOwnershipUpdate = (value: string) => {
    const newFilters = { ...businessesFilter };
    newFilters[value] = newFilters[value] ? undefined : true;
    if (newFilters['blackOwned'] && newFilters['indigenousOwned']) {
      // if (newFilters['blackOwned'] && newFilters['indigenousOwned'] && newFilters['majorityWomenOwned']) {
      // newFilters['majorityWomenOwned'] = undefined;
      newFilters['blackOwned'] = undefined;
      newFilters['indigenousOwned'] = undefined;
    }
    setBusinessesFilter(newFilters);
  };

  const handleCategoryUpdate = (category: string) => {
    let newCategories: string[] =
      filterCategories && filterCategories.length ? [...filterCategories] : [];
    if (newCategories.includes(category)) {
      newCategories = _.pull(newCategories, category);
    } else {
      newCategories.push(category);
    }
    setCursor(null);
    if (businesses.length <= 0 && newCategories.length === 1) {
      setSearchInput('');
      formik.setFieldValue('search', '');
    }
    setFilterCategories(newCategories);
  };

  const formik = useFormik({
    initialValues: {
      search: searchQuery || '',
    },
    onSubmit: handleSearchSubmit,
  });

  const filterOwnership = [
    businessesFilter.blackOwned ? 'blackOwned' : '',
    businessesFilter.indigenousOwned ? 'indigenousOwned' : '',
    // businessesFilter.majorityWomenOwned ? 'majorityWomenOwned' : '',
  ];

  return (
    <PageWrapper title="Shop All">
      <section className={`section-first inner-width ${styles.header}`}>
        <SearchHeader copy={doc} onMinimize={(minimize) => setMinimized(minimize)} />
      </section>
      <div
        ref={searchRef}
        className={`inner-width ${styles.sticky} ${minimized ? styles.collapse : ''
          }`}
      >
        <div className={styles.search}>
          <form className={styles.form} onSubmit={formik.handleSubmit}>
            <section className={styles.inputs}>
              <input
                type="text"
                className={styles.input}
                placeholder="Search"
                value={formik.values.search}
                onChange={handleSearchChange}
              />
              {searchInput && searchInput.length > 0 && (
                <button
                  className={`${styles.clear} button`}
                  type="button"
                  onClick={handleClearSearch}
                >
                  <CrossIcon />
                </button>
              )}
              {(!searchInput || searchInput.length === 0) &&
                formik.values.search.length > 0 && (
                  <button className={`${styles.submit} button`} type="submit">
                    <ButtonArrow brass />
                  </button>
                )}
            </section>
          </form>
          <div className={styles.toggles}>
            {showCorporationFilters ? (
              <FilterToggle
                label={filtersOpen ? 'Hide Filters' : 'Show Filters'}
                selected={filtersOpen}
                onSelected={() => setFiltersOpen(!filtersOpen)}
              />
            ) : (
              <>
                <FilterToggle
                  label="Ownership"
                  selected={filterType === FilterTypes.OWNERSHIP}
                  onSelected={() =>
                    setFilterType(
                      filterType !== FilterTypes.OWNERSHIP
                        ? FilterTypes.OWNERSHIP
                        : FilterTypes.NONE
                    )
                  }
                />
                <FilterToggle
                  label="Categories"
                  selected={filterType === FilterTypes.CATEGORIES}
                  onSelected={() =>
                    setFilterType(
                      filterType !== FilterTypes.CATEGORIES
                        ? FilterTypes.CATEGORIES
                        : FilterTypes.NONE
                    )
                  }
                />
              </>
            )}
            <FilterToggle
              label="Sort"
              selected={filterType === FilterTypes.SORT}
              onSelected={() =>
                setFilterType(
                  filterType !== FilterTypes.SORT
                    ? FilterTypes.SORT
                    : FilterTypes.NONE
                )
              }
            />
          </div>
        </div>
        <div className={styles.filters}>
          {filterType === FilterTypes.OWNERSHIP && !showCorporationFilters && (
            <PillFilter
              data={OwnershipOptions}
              minimized={false}
              selectedFilters={filterOwnership}
              onUpdate={(value) => {
                setCursor(null);
                if (window.scrollY >= filtersTop) {
                  window.scrollTo(0, filtersTop);
                }
                handleOwnershipUpdate(value);
              }}
            />
          )}
          {filterType === FilterTypes.CATEGORIES && !showCorporationFilters && (
            <PillFilter
              data={categories ? categories.data.categories_primary : null}
              minimized={false}
              selectedFilters={filterCategories}
              onUpdate={(category) => {
                setCursor(null);
                if (window.scrollY >= filtersTop) {
                  window.scrollTo(0, filtersTop);
                }
                handleCategoryUpdate(category);
              }}
            />
          )}
          {filterType === FilterTypes.SORT && (
            <LabelFilter
              data={SortOptions}
              minimized={false}
              selectedFilter={filterSort}
              onUpdate={(option) => {
                setCursor(null);
                if (window.scrollY >= filtersTop) {
                  window.scrollTo(0, filtersTop);
                }
                setFilterSort(option);
              }}
            />
          )}
        </div>
      </div>
      <section
        className={`inner-width inner-width-padding column ${styles.container} ${hasNextPage ? styles['padding-bottom'] : ''
          }`}
      >
        {showCorporationFilters && (
          <div
            ref={filtersRef}
            className={`${styles.sidebar} ${filtersOpen ? styles.show : ''}`}
            style={{ maxHeight: `${maxFiltersHeight}px`, top: `${filtersTop}px` }}
          >
            <div className={styles.wrapper}>
              <PillFilter
                label="Ownership"
                data={OwnershipOptions}
                minimized={false}
                selectedFilters={filterOwnership}
                onUpdate={(value) => {
                  setCursor(null);
                  if (window.scrollY >= filtersTop) {
                    window.scrollTo(0, filtersTop);
                  }
                  handleOwnershipUpdate(value);
                }}
              />
              <PillFilter
                label="Categories"
                data={categories ? categories.data.categories_primary : null}
                minimized={false}
                selectedFilters={filterCategories}
                onUpdate={(category) => {
                  setCursor(null);
                  if (window.scrollY >= filtersTop) {
                    window.scrollTo(0, filtersTop);
                  }
                  handleCategoryUpdate(category);
                }}
              />
              <SearchFilter
                businessesFilter={businessesFilter}
                setBusinessesFilter={(filters) => {
                  setCursor(null);
                  if (window.scrollY >= filtersTop) {
                    window.scrollTo(0, filtersTop);
                  }
                  setBusinessesFilter(filters);
                }}
              // setShouldUpdate={setShouldUpdate}
              />
            </div>
          </div>
        )}
        <div className={`full-width ${styles.grid}`} ref={containerRef}>
          {!loading && !showLoading && businesses.length <= 0 && (
            <section className={`inner-width ${styles.noresults}`}>
              <RichText render={doc.data.no_results} />
            </section>
          )}
          <>
            {businesses.map((item, index) => (
              <BusinessThumb
                key={`${index}`}
                rootRef={containerRef.current}
                item={item.node}
                lastBusinessRef={
                  index === businesses.length - 1 ? lastBusinessRef : null
                }
              />
            ))}
            {showLoading && (
              <div className={styles.loader}>
                <LoadingIndicator inline={true} />
              </div>
            )}
          </>
        </div>
        {error && (
          <div className="inner-width-padding row">
            <p className="error">{error.message}</p>
          </div>
        )}
      </section>
      <BackToTopButton />
    </PageWrapper>
  );
};

export async function getServerSideProps({ query }) {
  const client = Client();
  const doc = await client.getSingle('search', {});

  const categories = query.cat && query.cat.length ? query.cat.split(',') : null;
  const sort = query.sort ? query.sort : Sort.LATEST;
  // Pass data to the page via props
  return {
    props: {
      searchQuery: query.q ? query.q : null,
      filter: { sort, categories },
      doc,
    },
  };
}

export default SearchPage;
